import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { MPASIRecipe, FamilyRecipe, TwinCookRecipe } from './schemas/recipe';
import { IngredientDetectionResult } from './schemas/ingredient-detection';
import { categorizeIngredientLocal, standardizeAmountAndUnit, trackEvent } from './utils';
import { secureStorage } from './secure-storage';

export type PantryItemStatus = 'available' | 'low' | 'out';

export interface MealLog {
  id: string;
  date: number;
  recipeTitle: string;
  isBabyFed: boolean;
  babyPortionEaten?: 'none' | 'some' | 'most' | 'all';
  whoStars: {
    karbo: boolean;
    protein: boolean;
    sayur: boolean;
    lemak: boolean;
  };
}

export interface PantryItem {
  id: string;
  name: string;
  category?: string;
  amount?: string;
  amountValue?: number;
  amountUnit?: string;
  status: PantryItemStatus;
  lastUpdated: number;
  expiryDate?: number;
  isCut?: boolean;
}

interface PantryStore {
  items: PantryItem[];
  mealHistory: MealLog[];
  addItem: (item: { name: string; category?: string; amount?: string; value?: number; unit?: string; expiryDate?: number; isCut?: boolean }) => void;
  removeItem: (id: string) => void;
  updateStatus: (id: string, status: PantryItemStatus) => void;
  bulkAdd: (ingredients: IngredientDetectionResult['ingredients']) => void;
  deductFromRecipe: (recipe: MPASIRecipe | FamilyRecipe | TwinCookRecipe) => { updatedNames: string[]; outOfStock: string[] };
  logMeal: (log: Omit<MealLog, 'id' | 'date'>) => void;
  getCriticalLow: () => PantryItem[];
}

export const usePantryStore = create<PantryStore>()(
  persist(
    (set, get) => ({
      items: [],
      mealHistory: [],
      addItem: (item) => {
        trackEvent('pantry_updated', { action: 'add_item', name: item.name });
        const id = Math.random().toString(36).substring(7);
        const autoCategory = item.category || categorizeIngredientLocal(item.name);
        const { amount: stdAmount, value: stdValue, unit: stdUnit } = standardizeAmountAndUnit(item.amount || '');

        const newItem: PantryItem = {
          id,
          name: item.name,
          category: autoCategory,
          amount: stdAmount,
          amountValue: stdValue !== undefined ? stdValue : item.value,
          amountUnit: stdUnit || item.unit,
          status: 'available',
          lastUpdated: Date.now(),
          expiryDate: item.expiryDate,
          isCut: item.isCut,
        };
        set((state) => {
          const existing = state.items.find(i => i.name.toLowerCase() === item.name.toLowerCase());
          if (existing) {
             return {
               items: state.items.map(i => i.id === existing.id ? { ...newItem, id: existing.id } : i)
             };
          }
          return { items: [...state.items, newItem] };
        });
      },
      removeItem: (id) => {
        trackEvent('pantry_updated', { action: 'remove_item' });
        set((state) => ({
          items: state.items.filter((i) => i.id !== id),
        }));
      },
      updateStatus: (id, status) => {
        trackEvent('pantry_updated', { action: 'update_status', status });
        set((state) => ({
          items: state.items.map((i) =>
            i.id === id ? { ...i, status, lastUpdated: Date.now() } : i
          ),
        }));
      },
      bulkAdd: (ingredients) => {
        trackEvent('pantry_updated', { action: 'bulk_add', count: ingredients.length });
        set((state) => {
          const newItems = [...state.items];
          ingredients.forEach((ing) => {
            const index = newItems.findIndex((i) => i.name.toLowerCase() === ing.name.toLowerCase());
            
            const autoCategory = ing.category || categorizeIngredientLocal(ing.name);
            const rawAmount = ing.estimatedAmount ? `${ing.estimatedAmount.value} ${ing.estimatedAmount.unit}` : '';
            const { amount: stdAmount, value: stdValue, unit: stdUnit } = standardizeAmountAndUnit(rawAmount);

            const newItem: PantryItem = {
              id: index > -1 ? newItems[index].id : Math.random().toString(36).substring(7),
              name: ing.name,
              category: autoCategory,
              amount: stdAmount,
              amountValue: stdValue !== undefined ? stdValue : (ing.estimatedAmount?.value),
              amountUnit: stdUnit || (ing.estimatedAmount?.unit),
              status: 'available' as PantryItemStatus,
              lastUpdated: Date.now(),
            };
            if (index > -1) {
              newItems[index] = newItem;
            } else {
              newItems.push(newItem);
            }
          });
          return { items: newItems };
        });
      },
      deductFromRecipe: (recipe) => {
        const recipeIngredients: { name: string; amount: string }[] = [];
        const updatedNames: string[] = [];
        const outOfStock: string[] = [];
        
        if ('sharedIngredients' in recipe) {
          recipeIngredients.push(...recipe.sharedIngredients);
          recipeIngredients.push(...recipe.babyOnlyAdditions);
          recipeIngredients.push(...recipe.familyOnlyAdditions);
        } else if ('ingredients' in recipe) {
          recipeIngredients.push(...recipe.ingredients);
        }

        set((state) => {
          const newItems: PantryItem[] = state.items.map((pantryItem) => {
            const matchedIng = recipeIngredients.find((ing) => 
              pantryItem.name.toLowerCase().includes(ing.name.toLowerCase()) ||
              ing.name.toLowerCase().includes(pantryItem.name.toLowerCase())
            );

            if (matchedIng) {
               updatedNames.push(pantryItem.name);
               if (pantryItem.amountValue !== undefined) {
                  const amountStr = matchedIng.amount ? String(matchedIng.amount) : '0';
                  const usedValue = parseFloat(amountStr) || 0;
                  let convertedValue = usedValue;
                  if (pantryItem.amountUnit) {
                    const matchedUnitMatch = amountStr.match(/[a-zA-Z]+/);
                    const matchedUnit = matchedUnitMatch ? matchedUnitMatch[0].toLowerCase() : '';
                    const pantryUnit = pantryItem.amountUnit.toLowerCase();
                    if ((matchedUnit === 'g' || matchedUnit === 'gram' || matchedUnit === 'gr') && (pantryUnit === 'kg' || pantryUnit === 'kilogram')) {
                      convertedValue = usedValue / 1000;
                    } else if ((matchedUnit === 'ml' || matchedUnit === 'mililiter') && (pantryUnit === 'l' || pantryUnit === 'liter')) {
                      convertedValue = usedValue / 1000;
                    } else if ((matchedUnit === 'kg' || matchedUnit === 'kilogram') && (pantryUnit === 'g' || pantryUnit === 'gram' || pantryUnit === 'gr')) {
                      convertedValue = usedValue * 1000;
                    } else if ((matchedUnit === 'l' || matchedUnit === 'liter') && (pantryUnit === 'ml' || pantryUnit === 'mililiter')) {
                      convertedValue = usedValue * 1000;
                    } else if (matchedUnit !== pantryUnit && 
                               !((matchedUnit === 'g' || matchedUnit === 'gram' || matchedUnit === 'gr') && (pantryUnit === 'g' || pantryUnit === 'gram' || pantryUnit === 'gr')) &&
                               matchedUnit && pantryUnit && !['secukupnya', 'siung', 'buah', 'lembar', 'batang'].includes(matchedUnit)) {
                      convertedValue = 0; 
                    }
                  }
                  
                  const newValue = Math.max(0, pantryItem.amountValue - convertedValue);
                  const status: PantryItemStatus = newValue <= 0 ? 'out' : (newValue < pantryItem.amountValue * 0.2 ? 'low' : 'available');
                  if (status === 'out' || status === 'low') {
                    outOfStock.push(pantryItem.name);
                  }
                  return { ...pantryItem, amountValue: newValue, status, lastUpdated: Date.now() };
               } else {
                  const status: PantryItemStatus = 'low';
                  outOfStock.push(pantryItem.name);
                  return { ...pantryItem, status, lastUpdated: Date.now() };
               }
            }
            return pantryItem;
          });
          return { items: newItems };
        });

        return { updatedNames, outOfStock };
      },
      logMeal: (log) => {
        set((state) => ({
          mealHistory: [
            { id: Math.random().toString(36).substring(7), date: Date.now(), ...log },
            ...state.mealHistory
          ]
        }));
      },
      getCriticalLow: () => {
        return get().items.filter((i) => i.status === 'low' || i.status === 'out');
      },
    }),
    {
      name: 'satu-wajan-pantry',
      storage: createJSONStorage(() => secureStorage),
    }
  )
);
